import React, { FormEvent } from 'react'
import { Form } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import {
  QueryClient,
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLink, faEye, faEyeSlash, faTrashAlt } from '@fortawesome/free-solid-svg-icons'

import {
  FoodGroup,
  FoodRestriction,
  Ingredient,
  IngredientGroup,
  IngredientGroupListItem,
  IngredientGroupPartial,
  LanguagesConfig,
  NutrientRetentionFactor,
  WeightYieldFactor,
} from '@/api/types'
import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import { Grid } from '@/components/Grid'
import { Label } from '@/components/Label'
import { List, ListItem } from '@/components/List'
import { TextField } from '@/components/TextField'
import { PageEditHeader } from '@/components/PageEditHeader'
import { CreatableSelect, Select } from '@/components/Select'
import WeightYieldSelector from './WeightYieldSelector'
import NutrientRetentionFactorSelector from './NutrientRetentionFactorSelector'
import NutritionFacts from '../NutritionFacts'

import './EditIngredient.css'
import ServingSizes from './ServingSizes'
import {
  createIngredientGroup,
  ingredientGroupQuery,
  ingredientGroupsQuery,
} from '@/api/ingredients'
import { useSaveShortcut } from '@/hooks/useSaveShortcut'
import { useHistoryState } from '@/hooks/useHistoryState'
import { useAuth } from '@/auth'
import Languages from '../Languages/Languages'

type Props = {
  ingredient: Ingredient
  foodGroups: FoodGroup[]
  foodRestrictions: FoodRestriction[]
  languages: LanguagesConfig
  saveIngredient: UseMutationResult
  toggleVisibility: UseMutationResult
  duplicateIngredient: () => void
  deleteIngredient: () => void
}

function invalidateIngredientGroupQuery(
  ig: IngredientGroup | IngredientGroupPartial | undefined,
  queryClient: QueryClient,
) {
  if (ig?.idIngredientGroup) {
    queryClient.invalidateQueries({
      queryKey: ingredientGroupQuery(ig.idIngredientGroup).queryKey,
    })
  }
}

export default function EditIngredient({
  ingredient,
  foodGroups,
  foodRestrictions,
  languages,
  saveIngredient,
  toggleVisibility,
  duplicateIngredient,
  deleteIngredient,
}: Props) {
  const formRef = useSaveShortcut()
  const { t } = useTranslation()
  const [tempIngredient, setTempIngredient] = useHistoryState('ingredient', ingredient)
  const queryClient = useQueryClient()
  const ingredientGroups = useQuery(ingredientGroupsQuery).data ?? []
  const auth = useAuth()
  const isDisabled = !auth.isAdmin && !auth.isNutritionist && !auth.isAmbassador

  const ig = tempIngredient.ingredientGroup
  const ingredientGroup = ig ? { idIngredientGroup: ig.idIngredientGroup, name: ig.name } : null

  const createIngGroup = useMutation({
    mutationFn: (name: string) => {
      return createIngredientGroup({ name, ingredientIds: [], nameTranslations: [] })
    },
    onSuccess: (ig) => {
      queryClient.setQueryData(ingredientGroupsQuery.queryKey, (oldData) => {
        const item: IngredientGroupListItem = {
          idIngredientGroup: ig.idIngredientGroup!,
          ingredientCount: 0,
          name: ig.name,
        }
        if (Array.isArray(oldData)) {
          return [...oldData, item]
        } else {
          return [item]
        }
      })
      handleChangeIG({ idIngredientGroup: ig.idIngredientGroup!, name: ig.name })
    },
  })

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    saveIngredient.mutate(tempIngredient)
  }

  const handleChangeInput = (key: string, value: unknown) => {
    setTempIngredient({
      ...tempIngredient,
      [key]: value,
    })
  }

  const handleChangeIG = (ig?: IngredientGroupPartial) => {
    invalidateIngredientGroupQuery(tempIngredient.ingredientGroup, queryClient)
    invalidateIngredientGroupQuery(ig, queryClient)

    setTempIngredient({
      ...tempIngredient,
      ingredientGroup: ig,
    })
  }

  const handleToggleVisibility = () => {
    setTempIngredient({
      ...tempIngredient,
      hideFromUser: !tempIngredient.hideFromUser,
    })

    toggleVisibility.mutate(!tempIngredient.hideFromUser)
  }

  const updateFactors = (key: string, factors: (WeightYieldFactor | NutrientRetentionFactor)[]) => {
    setTempIngredient({
      ...tempIngredient,
      [key]: factors,
    })
  }

  const handleChangeTranslation = (languageCode: string, translation: string) => {
    if (languages && languages.defaultLanguage !== languageCode) {
      if (!tempIngredient.nameTranslations) {
        tempIngredient.nameTranslations = []
      }

      const translationIndex = tempIngredient.nameTranslations.findIndex(
        (t) => t.languageCode === languageCode,
      )
      if (translationIndex !== -1) {
        tempIngredient.nameTranslations[translationIndex].translation = translation
      } else {
        tempIngredient.nameTranslations.push({ languageCode, translation })
      }
    } else {
      tempIngredient.name = translation
    }

    setTempIngredient({
      ...tempIngredient,
    })
  }

  return (
    <Form onSubmit={handleSubmit} ref={formRef}>
      <PageEditHeader
        title={t('ingredient.editIngredient')}
        renderTitleActions={() => {
          if (isDisabled) {
            return null
          }
          return (
            <>
              <Button
                variant="icon"
                onPress={handleToggleVisibility}
                aria-label={tempIngredient.hideFromUser ? 'Show ingredient' : 'Hide ingredient'}>
                <FontAwesomeIcon
                  opacity={tempIngredient.hideFromUser ? 0.4 : 1}
                  icon={tempIngredient.hideFromUser ? faEyeSlash : faEye}
                />
              </Button>
              <Button asChild>
                <Link
                  to="/recipes"
                  search={{
                    idIngredient: ingredient.idIngredient,
                    ingredientName: ingredient.name,
                  }}>
                  {t('ingredient.showRecipes')}
                </Link>
              </Button>
              <Button onPress={duplicateIngredient}>{t('recipe.duplicate')}</Button>
            </>
          )
        }}>
        {ingredient.source !== 'EUROFIR' && (
          <Button variant="negative" onPress={deleteIngredient}>
            {t('recipe.deleteRecipe')}
          </Button>
        )}

        {!isDisabled && (
          <Button variant="primary" type="submit">
            {t('ingredient.saveIngredient')}
          </Button>
        )}
      </PageEditHeader>

      <Grid columns="1fr 320px">
        <div className="panel">
          <div>
            {languages && languages.languages.length > 0 ? (
              <Languages
                languages={languages}
                object={tempIngredient}
                handleChangeTranslation={handleChangeTranslation}
                errorMessage={t('ingredient.emptyName')}
              />
            ) : (
              <TextField
                isDisabled={isDisabled}
                isRequired
                label={t('ingredient.name')}
                value={tempIngredient.name}
                errorMessage={t('ingredient.emptyName')}
                onChange={(value) => handleChangeInput('name', value)}
              />
            )}
          </div>

          <div>
            <Label>
              {t('ingredient.ingredientGroup')}
              {!isDisabled && (
                <Link
                  style={{ marginLeft: 8 }}
                  to="/ingredients/displaynames/$ingId"
                  disabled={!ingredientGroup}
                  params={{ ingId: ingredientGroup?.idIngredientGroup + '' }}>
                  <FontAwesomeIcon icon={faExternalLink} size="xs" />
                </Link>
              )}
            </Label>

            <CreatableSelect
              options={ingredientGroups.map((ig) => ({
                idIngredientGroup: ig.idIngredientGroup,
                name: ig.name,
              }))}
              isDisabled={isDisabled}
              isClearable
              getOptionLabel={(option) => option.label ?? option.name}
              getOptionValue={(option) =>
                option.idIngredientGroup ? option.idIngredientGroup!.toString() : ''
              }
              value={ingredientGroup}
              formatCreateLabel={(name) => t('ingredient.createIngredientGroup', { name })}
              onChange={(ig) => {
                const ingGroup = ig
                  ? { idIngredientGroup: ig.idIngredientGroup, name: ig.name }
                  : undefined
                handleChangeIG(ingGroup)
              }}
              onCreateOption={(name) => {
                createIngGroup.mutate(name)
              }}
            />
          </div>

          <div>
            <Label>{t('ingredient.foodGroups')}</Label>

            <Select
              isDisabled={isDisabled}
              isMulti
              closeMenuOnSelect={false}
              options={foodGroups}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.idGroup.toString()}
              value={tempIngredient.groups}
              onChange={(foodGroups) => {
                handleChangeInput('groups', foodGroups)
              }}
            />
          </div>

          <div>
            <Label>{t('ingredient.foodRestrictions')}</Label>

            <Select
              isMulti
              isDisabled={isDisabled}
              closeMenuOnSelect={false}
              options={foodRestrictions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.idFoodRestriction.toString()}
              value={tempIngredient.foodRestrictions}
              onChange={(foodRestrictions) => {
                handleChangeInput('foodRestrictions', foodRestrictions)
              }}
            />
          </div>
          <div>
            <Label>{t('ingredient.originalName')}</Label>
            <div className="fieldValue">{ingredient.originalName}</div>
          </div>

          <div>
            <Label>{t('ingredient.englishName')}</Label>
            <div className="fieldValue">{ingredient.englishName}</div>
          </div>

          <div>
            <Label>{t('ingredient.externalIdentifier')}</Label>
            <div className="fieldValue">{ingredient.externalID}</div>
          </div>

          {/* <FactorsList
            idKey="idWeightYieldFactor"
            label={t('ingredient.weightYieldFactors')}
            emptyLabel={t('ingredient.weightYieldFactorsEmpty')}
            factors={tempIngredient.weightYieldFactors}
            onRemoveFactor={(factors) => updateFactors('weightYieldFactors', factors)}>
            {!isDisabled && (
              <WeightYieldSelector
                selection={tempIngredient.weightYieldFactors}
                onSelectFactors={(factors) => updateFactors('weightYieldFactors', factors)}
              />
            )}
          </FactorsList>

          <FactorsList
            idKey="idNutrientRetentionFactor"
            label={t('ingredient.nutritionRetentionFactors')}
            emptyLabel={t('ingredient.nutritionRetentionFactorsEmpty')}
            factors={tempIngredient.nutrientRetentionFactors}
            onRemoveFactor={(factors) => updateFactors('nutrientRetentionFactors', factors)}>
            {!isDisabled && (
              <NutrientRetentionFactorSelector
                selection={tempIngredient.nutrientRetentionFactors}
                onSelectFactors={(factors) => updateFactors('nutrientRetentionFactors', factors)}
              />
            )}
          </FactorsList> */}

          <div className="LangualDescriptors">
            <Label>{t('ingredient.descriptors')}</Label>

            <List
              items={ingredient.languaLDescriptors}
              emptyLabel={t('ingredient.descriptorsEmpty')}>
              {(item) => (
                <ListItem key={item.id}>
                  {!isDisabled ? (
                    <Link
                      to="/ingredients/fcdb"
                      search={{ languaLID: [item.id] }}
                      className="LangualDescriptorsList-link">
                      <span className="LangualDescriptorsList-id">{item.id}</span>
                      <span className="LangualDescriptorsList-description">
                        {item.translatedDescription || item.description}
                      </span>
                    </Link>
                  ) : (
                    <span>
                      <span className="LangualDescriptorsList-id">{item.id}</span>{' '}
                      <span className="LangualDescriptorsList-description">
                        {item.translatedDescription || item.description}
                      </span>
                    </span>
                  )}
                </ListItem>
              )}
            </List>
          </div>
        </div>
        <div className="stack-y">
          <NutritionFacts nutritionalValue={ingredient.in100g} />
          <ServingSizes ingredient={ingredient} isDisabled={isDisabled} />
        </div>
      </Grid>
    </Form>
  )
}

type FactorType = WeightYieldFactor | NutrientRetentionFactor

type FactorsListProps = {
  idKey: string
  label: string
  isDisabled?: boolean
  emptyLabel: string
  factors: FactorType[]
  children: React.ReactNode
  onRemoveFactor: (factors: FactorType[]) => void
}

function FactorsList({
  idKey,
  label,
  isDisabled,
  emptyLabel,
  factors,
  children,
  onRemoveFactor,
}: FactorsListProps) {
  const handleRemove = (id: number) => {
    onRemoveFactor(factors.filter((rt) => rt[idKey] !== id))
  }

  return (
    <div>
      <Label>{label}</Label>

      <List items={factors} emptyLabel={emptyLabel}>
        {(item) => (
          <ListItem key={item[idKey]}>
            {item.label ? `${item.category} | ${item.label}` : `${item.category}`}
            {!isDisabled && (
              <Button
                variant="icon"
                onPress={() => handleRemove(item[idKey])}
                style={{ padding: 0 }}>
                <FontAwesomeIcon icon={faTrashAlt} color="var(--invalid-color)" />
              </Button>
            )}
          </ListItem>
        )}
      </List>

      <Flex justify="end">{children}</Flex>
    </div>
  )
}
